import React from 'react';
import PropTypes from 'prop-types';
import styled, { css, ThemeProvider } from 'styled-components';
import valueMap from '../utils/valueMap';
import { colors, fontWeight, ReskinThemes } from '../constants';
import { clearFix, padding } from 'polished';
import CardTable from './card_table';
import PrimaryPalette from '../../birch/colors';

const azaleaTheme = {
  header: {
    borderStyles: '',
    color: colors.darkestGrey,
    fontSize: '18px',
  },
  container: {
    backgroundColor: colors.lightestGrey,
    borderColor: colors.lightGrey,
  },
  body: {
    backgroundColor: colors.white,
    borderColor: colors.lightGrey,
    emptyStateColor: colors.darkGrey,
  },
};

const birchTheme = {
  header: {
    borderStyles: 'border-bottom: none;',
    color: PrimaryPalette.mediumEvergreen,
    fontSize: '20px',
  },
  container: {
    backgroundColor: PrimaryPalette.white,
    borderColor: PrimaryPalette.darkGrey,
  },
  body: {
    backgroundColor: PrimaryPalette.white,
    borderColor: PrimaryPalette.darkGrey,
    emptyStateColor: PrimaryPalette.darkGrey,
  },
};

const themeSelector = (themeName) => {
  return themeName !== ReskinThemes.BIRCH ? azaleaTheme : birchTheme;
};

const validHeaderLabelSizes = ['small', 'large'];

class CardHeader extends React.Component {
  static propTypes = {
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    leftComponent: PropTypes.element,
    rightComponent: PropTypes.element,
    wrapRightComponent: PropTypes.bool,
    labelSize: PropTypes.oneOf(validHeaderLabelSizes),
    style: PropTypes.object,
    className: PropTypes.string,
  };

  static defaultProps = {
    labelSize: 'large',
    wrapRightComponent: true,
    style: {},
  };

  renderRightComponent() {
    const { rightComponent, wrapRightComponent } = this.props;

    if (!rightComponent) {
      return null;
    }

    if (wrapRightComponent) {
      return (
        <RightComponentWrapper>
          <div>{rightComponent}</div>
        </RightComponentWrapper>
      );
    } else {
      return rightComponent;
    }
  }

  render() {
    // rightComponent && the wrap Property are not passed through
    const {
      label,
      labelSize,
      leftComponent,
      style,
      rightComponent,
      wrapRightComponent,
      ...rest
    } = this.props;

    return (
      <CardHeaderContainer style={style} {...rest}>
        {leftComponent ? (
          <div>{leftComponent}</div>
        ) : (
          <CardHeaderLabel labelSize={labelSize}>{label}</CardHeaderLabel>
        )}

        {this.renderRightComponent()}
      </CardHeaderContainer>
    );
  }
}

const CardHeaderContainer = styled.header`
  border-radius: 3px;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  background-color: ${(props) =>
    themeSelector(props.theme?.name).container.backgroundColor};
  border: 1px solid
    ${(props) => themeSelector(props.theme?.name).container.borderColor};
  padding: 10px ${(props) => props.cardGutter};
  position: relative;
  box-sizing: border-box;
  line-height: 35px;
  display: flex;
  align-content: center;
  justify-content: space-between;
  ${(props) =>
    !props.keepBorderBottom &&
    themeSelector(props.theme?.name).header.borderStyles}
`;

CardHeaderContainer.displayName = 'CardHeaderContainer';

CardHeaderContainer.propTypes = {
  cardGutter: PropTypes.string,
};

CardHeaderContainer.defaultProps = {
  cardGutter: '24px',
};

const labelSize = valueMap(validHeaderLabelSizes, 'labelSize');

const CardHeaderLabel = styled.h3`
  display: inline-block;
  margin: 0;
  line-height: inherit;
  color: ${(props) => themeSelector(props.theme?.name).header.color};

  ${labelSize.small`
    font-size: 14px;
  `}

  ${labelSize.large`
    font-size: ${(props) => themeSelector(props.theme?.name).header.fontSize};
    ${fontWeight('normal')}
  `}

  ${(props) => props.active && 'cursor: pointer;'}
`;

CardHeaderLabel.propTypes = {
  labelSize: PropTypes.oneOf(validHeaderLabelSizes),
};

CardHeaderLabel.defaultProps = {
  labelSize: 'large',
};

const RightComponentWrapper = styled.div`
  float: right;
  line-height: inherit;
`;

RightComponentWrapper.displayName = 'RightComponentWrapper';

const bottomMarginSizes = {
  none: 0,
  default: '2em',
};

const CardPaddingTheme = (props) => {
  const { useLegacyPadding, children } = props;

  const theme = {
    legacyPadding: useLegacyPadding,
  };

  return (
    <ThemeProvider theme={theme} {...props}>
      {children}
    </ThemeProvider>
  );
};

const Card = (props) => {
  const { useLegacyPadding } = props;
  let cardTheme = !(
    useLegacyPadding === undefined || useLegacyPadding === false
  );

  return (
    <CardPaddingTheme useLegacyPadding={cardTheme} {...props}>
      <StyledCardWrapper bottomMargin={props.bottomMargin} {...props}>
        {props.children}
      </StyledCardWrapper>
    </CardPaddingTheme>
  );
};

Card.propTypes = {
  bottomMargin: PropTypes.oneOf(Object.keys(bottomMarginSizes)),
};

Card.defaultProps = {
  bottomMargin: 'default',
};

Card.displayName = 'Card';

const verticalPaddingSizes = {
  none: 0,
  small: `0.5em`,
  medium: `1em`,
  large: `2em`,
};

const StyledCardWrapper = styled.div`
  margin-bottom: ${(props) => bottomMarginSizes[props.bottomMargin]};
`;

const CardBody = styled.div`
  box-sizing: border-box;
  background-color: ${(props) =>
    themeSelector(props.theme?.name).body.backgroundColor};
  border: 1px solid
    ${(props) => themeSelector(props.theme?.name).body.borderColor};
  border-top: 0;
  border-radius: ${(props) =>
    props.theme?.name !== ReskinThemes.BIRCH ? `3` : `5`}px;

  border-top-left-radius: 0;
  border-top-right-radius: 0;

  ${(props) =>
    props.theme.legacyPadding &&
    padding(
      verticalPaddingSizes[props.verticalCardGutter] /* top and bottom */,
      !props.compact && props.cardGutter /* left and right */
    )}

  ${(props) =>
    !props.theme.legacyPadding &&
    css`
      padding: 24px 24px;
    `}

  ${(props) =>
    !props.theme.legacyPadding &&
    props.children.type === CardTable &&
    css`
      padding: 0 24px 24px;
    `}

  ${clearFix()}

  .empty-state {
    .header {
      font-weight: 600;
    }
    color: ${colors.darkGrey};
    font-size: 14px;
  }
`;

CardBody.propTypes = {
  verticalCardGutter: PropTypes.oneOf(Object.keys(verticalPaddingSizes)),
  compact: PropTypes.bool,
  cardGutter: PropTypes.string,
};

CardBody.defaultProps = {
  verticalCardGutter: 'large',
  compact: false,
  cardGutter: '20px',
};

CardBody.displayName = 'CardBody';

export {
  Card,
  CardHeader,
  CardBody,
  CardHeaderLabel,
  CardHeaderContainer,
  verticalPaddingSizes,
  validHeaderLabelSizes,
};
